/**
 * MorphingBottomBar — Select-mode action bar that slides up over CanvasBottomNav.
 *
 * Normal mode: Hidden (CanvasBottomNav visible at z-40).
 * Select mode: Slides up at z-45 with teal accent sweep + staggered icon bloom.
 *
 * Phase state machine:
 *   hidden → pre-enter → entering (300ms) → visible
 *   visible → leaving (220ms) → hidden
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { T } from '@/styles/tokens';

type Phase = 'hidden' | 'pre-enter' | 'entering' | 'visible' | 'leaving';

interface MorphingBottomBarProps {
  selectMode: boolean;
  selCount: number;
  onMarkSold: () => void;
  onDelete: () => void;
}

export function MorphingBottomBar({
  selectMode,
  selCount,
  onMarkSold,
  onDelete,
}: MorphingBottomBarProps) {
  const [phase, setPhase] = useState<Phase>('hidden');
  const prevSelectMode = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (selectMode && !prevSelectMode.current) {
      clearTimers();
      // Step 1: Render off-screen (pre-enter)
      setPhase('pre-enter');
      // Step 2: Next frame → trigger transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase('entering');
          // Step 3: After animation completes → stable
          const t = setTimeout(() => setPhase('visible'), 340);
          timersRef.current.push(t);
        });
      });
    } else if (!selectMode && prevSelectMode.current) {
      clearTimers();
      setPhase('leaving');
      const t = setTimeout(() => setPhase('hidden'), 420);
      timersRef.current.push(t);
    }
    prevSelectMode.current = selectMode;
  }, [selectMode, clearTimers]);

  // Cleanup on unmount
  useEffect(() => clearTimers, [clearTimers]);

  // Don't render when hidden
  if (phase === 'hidden') return null;

  const disabled = selCount === 0;

  const selectActions = [
    { label: 'Mark Sold', action: onMarkSold },
    { label: 'Delete', action: onDelete },
  ];

  // Bar slide animation
  const isUp = phase === 'entering' || phase === 'visible';
  const barTransform = isUp
    ? 'translateY(0) scale(1)'
    : phase === 'leaving'
      ? 'translateY(110%) scale(0.96)'
      : 'translateY(100%) scale(1)';
  const barOpacity = isUp ? 1 : 0;
  const barTransition =
    phase === 'entering'
      ? 'transform 300ms cubic-bezier(0.34, 1.4, 0.64, 1), opacity 200ms ease'
      : phase === 'leaving'
        ? 'transform 360ms cubic-bezier(0.4, 0, 0.7, 0.2), opacity 280ms ease 60ms'
        : 'none';

  // Teal accent sweep
  const accentWidth = isUp ? '100%' : '0%';
  const accentTransition = isUp
    ? 'width 350ms cubic-bezier(0.25, 1, 0.5, 1) 80ms'
    : 'width 200ms cubic-bezier(0.4, 0, 1, 1)';

  // Icon bloom styles (staggered entrance, reverse-staggered exit)
  const iconCount = selectActions.length;
  function iconStyle(i: number): React.CSSProperties {
    if (phase === 'pre-enter') {
      return { transform: 'scale(0.4) translateY(12px)', opacity: 0, transition: 'none' };
    }
    if (phase === 'entering') {
      return {
        transform: 'scale(1) translateY(0)',
        opacity: 1,
        transition: `transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1) ${120 + i * 60}ms, opacity 200ms ease ${120 + i * 60}ms`,
      };
    }
    if (phase === 'visible') {
      return { transform: 'scale(1) translateY(0)', opacity: 1 };
    }
    // leaving — reverse stagger (last icon exits first)
    const ri = (iconCount - 1 - i) * 50;
    return {
      transform: 'scale(0.3) translateY(14px)',
      opacity: 0,
      transition: `transform 220ms cubic-bezier(0.6, 0, 0.7, 0.2) ${ri}ms, opacity 180ms ease ${ri}ms`,
    };
  }

  return (
    <nav style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 201,
      background: T.white,
      borderTop: `1px solid ${T.border}`,
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      transform: barTransform,
      opacity: barOpacity,
      transition: barTransition,
    }}>
      {/* Teal accent line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: 2,
        width: accentWidth,
        background: T.primary,
        transition: accentTransition,
      }} />

      <div style={{
        maxWidth: 420,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        height: 64,
        position: 'relative',
      }}>
        {selectActions.map((item, i) => (
          <button
            key={item.label}
            onClick={disabled ? undefined : item.action}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              width: '100%',
              height: '100%',
              background: 'none',
              border: 'none',
              cursor: disabled ? 'default' : 'pointer',
              color: disabled ? '#D4D1CB' : T.textSecondary,
              ...iconStyle(i),
            }}
          >
            {item.label === 'Price List' && (
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="8" y1="8" x2="16" y2="8" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="8" y1="16" x2="13" y2="16" />
              </svg>
            )}
            {item.label === 'Mark Sold' && (
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
              </svg>
            )}
            {item.label === 'Delete' && (
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <polyline points="3,6 5,6 21,6" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            )}
            <span style={{ fontSize: 10, fontWeight: 600 }}>{item.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}
